/*
  File: src/pages/RekognitionManagement.jsx
  Purpose: Manage Rekognition datasets, images, and labels
  Features:
  - List datasets
  - Create new datasets
  - Navigate to dataset details for images and labels management
*/
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import ManifestModal from '../components/dataset/modals/ManifestModal';
import ManifestResultModal from '../components/dataset/modals/ManifestResultModal';

function RekognitionManagement() {
    const navigate = useNavigate();
    const [datasets, setDatasets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newDatasetName, setNewDatasetName] = useState('');
    const [creating, setCreating] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const pageSize = 10;
    
    // Manifest generation state
    const [showManifestModal, setShowManifestModal] = useState(false);
    const [selectedDataset, setSelectedDataset] = useState(null);
    const [manifestLoading, setManifestLoading] = useState(false);
    const [manifestResult, setManifestResult] = useState(null);
    const [showManifestResultModal, setShowManifestResultModal] = useState(false);
    
    // Clone dataset state
    const [showCloneModal, setShowCloneModal] = useState(false);
    const [cloneDatasetName, setCloneDatasetName] = useState('');
    const [cloning, setCloning] = useState(false);
    const [datasetToClone, setDatasetToClone] = useState(null);
    
    // Dropdown state for each dataset
    const [openDropdown, setOpenDropdown] = useState(null);

    const rawBase = (process.env.REACT_APP_API_BASE || '').trim();
    const cleanedBase = rawBase.replace(/^@+/, '');
    const API_BASE = cleanedBase || '/dev';

    // Fetch datasets with pagination
    const fetchDatasets = async (page = 1) => {
        setLoading(true);
        setError('');
        try {
            const url = `${API_BASE}/rekog/datasets?page=${page}&page_size=${pageSize}`;
            const res = await fetch(url, {
                headers: { accept: 'application/json' },
                credentials: 'include'
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            
            // Handle both new paginated response and legacy array response
            if (data && typeof data === 'object' && data.items && data.pagination) {
                // New paginated response
                setDatasets(Array.isArray(data.items) ? data.items : []);
                setTotalCount(data.pagination.total_count);
            } else if (Array.isArray(data)) {
                // Legacy array response - fallback
                setDatasets(data);
                // Try to get total from headers for backward compatibility
                const totalFromHeader = res.headers.get('X-Total-Count');
                if (totalFromHeader) {
                    setTotalCount(parseInt(totalFromHeader, 10));
                } else {
                    setTotalCount(data.length);
                }
            } else {
                setDatasets([]);
                setTotalCount(0);
            }
        } catch (e) {
            setError('Failed to load datasets');
            setDatasets([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    };

    // Create new dataset
    const createDataset = async () => {
        if (!newDatasetName.trim()) return;
        
        setCreating(true);
        try {
            const res = await fetch(`${API_BASE}/rekog/datasets`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    accept: 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({ name: newDatasetName.trim() })
            });
            
            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP ${res.status}`);
            }
            
            // Refresh datasets list
            await fetchDatasets(currentPage);
            setShowCreateModal(false);
            setNewDatasetName('');
        } catch (e) {
            setError(e.message || 'Failed to create dataset');
        } finally {
            setCreating(false);
        }
    };

    // Generate manifest for a dataset
    const generateManifest = async (projectName, includeUnlabeled = false) => {
        if (!selectedDataset) return;
        
        setManifestLoading(true);
        try {
            const res = await fetch(`${API_BASE}/rekog/datasets/${selectedDataset.id}/manifest`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    accept: 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({ 
                    project_name: projectName,
                    dataset_type: 'train',
                    include_unlabeled: !!includeUnlabeled
                })
            });
            
            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP ${res.status}`);
            }
            
            const data = await res.json();
            setManifestResult(data);
            setShowManifestModal(false);
            setShowManifestResultModal(true);
            
        } catch (e) {
            setError(e.message || 'Failed to generate manifest');
        } finally {
            setManifestLoading(false);
        }
    };

    // Handle manifest generation request
    const handleGenerateManifest = (dataset) => {
        setSelectedDataset(dataset);
        setShowManifestModal(true);
        setError(''); // Clear any previous errors
    };

    // Clone dataset
    const cloneDataset = async () => {
        if (!cloneDatasetName.trim() || !datasetToClone) return;
        
        setCloning(true);
        try {
            const res = await fetch(`${API_BASE}/rekog/datasets/${datasetToClone.id}/clone`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    accept: 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({ name: cloneDatasetName.trim() })
            });
            
            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP ${res.status}`);
            }
            
            // Refresh datasets list
            await fetchDatasets(currentPage);
            setShowCloneModal(false);
            setCloneDatasetName('');
            setDatasetToClone(null);
        } catch (e) {
            setError(e.message || 'Failed to clone dataset');
        } finally {
            setCloning(false);
        }
    };

    // Handle clone request
    const handleCloneDataset = (dataset) => {
        setDatasetToClone(dataset);
        setCloneDatasetName(`${dataset.name} (Copy)`);
        setShowCloneModal(true);
        setOpenDropdown(null); // Close dropdown
        setError(''); // Clear any previous errors
    };

    useEffect(() => {
        fetchDatasets(currentPage);
    }, [currentPage]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openDropdown && !event.target.closest('.dataset-dropdown')) {
                setOpenDropdown(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openDropdown]);

    // Handle page change
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    // Calculate pagination info
    const totalPages = Math.ceil(totalCount / pageSize);
    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalCount);

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#0a0e1a] via-[#1a1f2e] to-[#2a2f3e]">
            <Navbar />
            <main className="pt-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <h1 className="text-3xl font-bold text-white">Rekognition Management</h1>
                            <p className="text-white/60 mt-2">Manage datasets, images, and labels for computer vision models</p>
                        </div>
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#7b3ff2] to-[#9060ff] hover:from-[#9060ff] hover:to-[#7b3ff2] text-white text-sm font-medium transition-all duration-200 hover:scale-105"
                        >
                            + Create Dataset
                        </button>
                    </div>

                    {/* Error Display */}
                    {error && (
                        <div className="mb-6 text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                            {error}
                        </div>
                    )}

                    {/* Loading State */}
                    {loading && (
                        <div className="flex items-center justify-center py-12">
                            <div className="text-[#4cc3e9] text-sm font-medium flex items-center gap-2">
                                <div className="w-4 h-4 border-2 border-[#4cc3e9]/30 border-t-[#4cc3e9] rounded-full animate-spin"></div>
                                Loading datasets...
                            </div>
                        </div>
                    )}

                    {/* Datasets Grid */}
                    {!loading && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {datasets.length === 0 ? (
                                <div className="col-span-full text-center py-12">
                                    <div className="text-white/60 text-lg mb-4">No datasets found</div>
                                    <button
                                        onClick={() => setShowCreateModal(true)}
                                        className="px-6 py-3 rounded-lg bg-gradient-to-r from-[#7b3ff2] to-[#9060ff] hover:from-[#9060ff] hover:to-[#7b3ff2] text-white text-sm font-medium transition-all duration-200"
                                    >
                                        Create Your First Dataset
                                    </button>
                                </div>
                            ) : (
                                datasets.map((dataset) => (
                                    <div
                                        key={dataset.id}
                                        className="bg-gradient-to-br from-[#1a2448]/80 to-[#1a2448]/60 rounded-xl p-6 border border-[#4cc3e9]/30 hover:border-[#4cc3e9]/50 transition-all duration-200 cursor-pointer hover:scale-105"
                                    >
                                        <div className="flex items-start justify-between mb-4">
                                            <h3 className="text-lg font-semibold text-white truncate pr-2">{dataset.name}</h3>
                                            <div className="flex items-center gap-2">
                                                <div className="flex items-center gap-1">
                                                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                                                    <span className="text-xs text-green-300">Active</span>
                                                </div>
                                                
                                                {/* Three-dot dropdown menu */}
                                                <div className="relative dataset-dropdown">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setOpenDropdown(openDropdown === dataset.id ? null : dataset.id);
                                                        }}
                                                        className="p-1 rounded hover:bg-white/10 transition-colors"
                                                    >
                                                        <svg className="w-4 h-4 text-white/60" fill="currentColor" viewBox="0 0 20 20">
                                                            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                                        </svg>
                                                    </button>
                                                    
                                                    {openDropdown === dataset.id && (
                                                        <div className="absolute right-0 top-full mt-1 w-32 bg-gray-800 border border-white/10 rounded-lg shadow-lg z-10">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleCloneDataset(dataset);
                                                                }}
                                                                className="w-full text-left px-3 py-2 text-sm text-white hover:bg-white/10 transition-colors flex items-center gap-2"
                                                            >
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                                </svg>
                                                                Clone
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between text-sm">
                                                <span className="text-white/60">Images:</span>
                                                <span className="text-white font-medium">{dataset.images_count || 0}</span>
                                            </div>
                                            <div className="flex items-center justify-between text-sm">
                                                <span className="text-white/60">Labels:</span>
                                                <span className="text-white font-medium">{dataset.labels_count || 0}</span>
                                            </div>
                                            <div className="flex items-center justify-between text-sm">
                                                <span className="text-white/60">Annotations:</span>
                                                <span className="text-white font-medium">{dataset.annotations_count || 0}</span>
                                            </div>
                                        </div>

                                        {dataset.created_at && (
                                            <div className="mt-4 pt-4 border-t border-white/10">
                                                <div className="text-xs text-white/60">
                                                    Created: {new Date(dataset.created_at).toLocaleDateString()}
                                                </div>
                                            </div>
                                        )}

                                        {/* Action buttons */}
                                        <div className="mt-4 pt-4 border-t border-white/10 flex items-center gap-2">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/rekognition-management/${dataset.id}`);
                                                }}
                                                className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#7b3ff2] to-[#9060ff] hover:from-[#9060ff] hover:to-[#7b3ff2] text-white text-xs font-medium transition-all duration-200 flex-1"
                                            >
                                                Manage
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleGenerateManifest(dataset);
                                                }}
                                                className="px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs font-medium transition-colors flex items-center gap-1"
                                                disabled={manifestLoading}
                                            >
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                                Manifest
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}

                    {/* Pagination */}
                    {!loading && datasets.length > 0 && totalPages > 1 && (
                        <div className="mt-8 flex items-center justify-between">
                            <div className="text-sm text-white/60">
                                Showing {startItem} to {endItem} of {totalCount} datasets
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                
                                {/* Page numbers */}
                                <div className="flex items-center gap-1">
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                                        // Show first page, last page, current page, and pages around current
                                        const showPage = 
                                            page === 1 || 
                                            page === totalPages || 
                                            Math.abs(page - currentPage) <= 1;
                                        
                                        if (!showPage && page === 2 && currentPage > 4) {
                                            return <span key={page} className="text-white/60 px-2">...</span>;
                                        }
                                        if (!showPage && page === totalPages - 1 && currentPage < totalPages - 3) {
                                            return <span key={page} className="text-white/60 px-2">...</span>;
                                        }
                                        if (!showPage) {
                                            return null;
                                        }
                                        
                                        return (
                                            <button
                                                key={page}
                                                onClick={() => handlePageChange(page)}
                                                className={`px-3 py-2 rounded-lg transition-colors ${
                                                    page === currentPage
                                                        ? 'bg-gradient-to-r from-[#7b3ff2] to-[#9060ff] text-white'
                                                        : 'bg-white/10 border border-white/20 text-white hover:bg-white/20'
                                                }`}
                                            >
                                                {page}
                                            </button>
                                        );
                                    })}
                                </div>
                                
                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Create Dataset Modal */}
                    {showCreateModal && (
                        <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                            <div className="relative w-full max-w-md bg-gradient-to-br from-[#0a1628] to-[#1a2448] rounded-xl border border-[#4cc3e9]/30 p-6">
                                <h2 className="text-xl font-semibold text-white mb-4">Create New Dataset</h2>
                                
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-white/80 mb-2">
                                        Dataset Name
                                    </label>
                                    <input
                                        type="text"
                                        value={newDatasetName}
                                        onChange={(e) => setNewDatasetName(e.target.value)}
                                        placeholder="Enter dataset name..."
                                        className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-[#4cc3e9]/50 focus:outline-none transition-colors"
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter' && !creating) {
                                                createDataset();
                                            }
                                        }}
                                    />
                                </div>

                                {error && (
                                    <div className="mb-4 text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded p-2">
                                        {error}
                                    </div>
                                )}

                                <div className="flex items-center justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowCreateModal(false);
                                            setNewDatasetName('');
                                            setError('');
                                        }}
                                        className="px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors"
                                        disabled={creating}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={createDataset}
                                        disabled={creating || !newDatasetName.trim()}
                                        className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#7b3ff2] to-[#9060ff] hover:from-[#9060ff] hover:to-[#7b3ff2] text-white font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {creating ? 'Creating...' : 'Create Dataset'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Manifest Modal */}
                    <ManifestModal
                        isOpen={showManifestModal}
                        onClose={() => {
                            setShowManifestModal(false);
                            setSelectedDataset(null);
                        }}
                        onGenerate={generateManifest}
                        loading={manifestLoading}
                        dataset={selectedDataset}
                    />

                    {/* Manifest Result Modal */}
                    <ManifestResultModal
                        isOpen={showManifestResultModal}
                        onClose={() => {
                            setShowManifestResultModal(false);
                            setManifestResult(null);
                        }}
                        result={manifestResult}
                    />

                    {/* Clone Dataset Modal */}
                    {showCloneModal && (
                        <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                            <div className="relative w-full max-w-md bg-gradient-to-br from-[#0a1628] to-[#1a2448] rounded-xl border border-[#4cc3e9]/30 p-6">
                                <h2 className="text-xl font-semibold text-white mb-4">Clone Dataset</h2>
                                
                                <div className="mb-4">
                                    <p className="text-sm text-white/70 mb-3">
                                        Clone dataset "{datasetToClone?.name}" with all its images, labels, and annotations.
                                    </p>
                                    <label className="block text-sm font-medium text-white/80 mb-2">
                                        New Dataset Name
                                    </label>
                                    <input
                                        type="text"
                                        value={cloneDatasetName}
                                        onChange={(e) => setCloneDatasetName(e.target.value)}
                                        placeholder="Enter new dataset name..."
                                        className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-[#4cc3e9]/50 focus:outline-none transition-colors"
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter' && !cloning) {
                                                cloneDataset();
                                            }
                                        }}
                                    />
                                </div>

                                {error && (
                                    <div className="mb-4 text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded p-2">
                                        {error}
                                    </div>
                                )}

                                <div className="flex items-center justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowCloneModal(false);
                                            setCloneDatasetName('');
                                            setDatasetToClone(null);
                                            setError('');
                                        }}
                                        className="px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors"
                                        disabled={cloning}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={cloneDataset}
                                        disabled={cloning || !cloneDatasetName.trim()}
                                        className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#7b3ff2] to-[#9060ff] hover:from-[#9060ff] hover:to-[#7b3ff2] text-white font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {cloning ? 'Cloning...' : 'Clone Dataset'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}

export default RekognitionManagement;
